import { useState } from "react";
import { toast } from "sonner";
import { Plus, CheckCircle2, XCircle, ArrowRightCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, EmptyState } from "@/components/admin/AdminUI";
import { Status, fmt, useList, Field, inputCls, COUNTRY } from "@/components/admin/finance/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const canValidate = (u) => ["admin", "direction"].includes(u?.role);

export default function Requests() {
  const { user } = useAuth();
  const { data, loading, reload } = useList(`/finance/requests?country=${COUNTRY}`);
  const cats = useList(`/finance/categories?country=${COUNTRY}`).data;
  const [form, setForm] = useState(null);

  const openNew = () => setForm({ amount: "", category: "", motif: "", beneficiaire: "", desired_date: "", description: "", currency: "FCFA", submit: true });
  const save = async () => {
    if (!form.amount || !form.motif) return toast.error("Montant et motif requis");
    try { await api.post("/finance/requests", { ...form, country: COUNTRY }); toast.success("Demande envoyée"); setForm(null); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const decide = async (id, decision) => {
    const reason = decision === "refusee" ? prompt("Motif du refus ?") : "";
    if (decision === "refusee" && !reason) return;
    try { await api.post(`/finance/requests/${id}/decision`, { decision, reason }); toast.success("Décision enregistrée"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const convert = async (id) => {
    try { const { data } = await api.post(`/finance/requests/${id}/convert`); toast.success(`Convertie en dépense ${data.reference}`); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex justify-end mb-4"><button onClick={openNew} data-testid="request-add-button" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#3B82F6] text-white text-sm font-semibold"><Plus className="w-4 h-4" /> Nouvelle demande</button></div>
      <Card className="overflow-hidden">
        {loading ? <EmptyState text="Chargement…" /> : data.length === 0 ? <EmptyState text="Aucune demande." /> : (
          <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="requests-table">
            <thead><tr className="border-b border-[#1E293B] text-left text-slate-400"><th className="px-4 py-3">Référence</th><th className="px-4 py-3">Motif</th><th className="px-4 py-3">Montant</th><th className="px-4 py-3">Demandeur</th><th className="px-4 py-3">Statut</th><th className="px-4 py-3"></th></tr></thead>
            <tbody>{data.map((r) => (
              <tr key={r.id} className="border-b border-[#1E293B]/60">
                <td className="px-4 py-3 text-white font-mono text-xs">{r.reference}</td>
                <td className="px-4 py-3 text-slate-300">{r.motif}<div className="text-xs text-slate-500">{r.category}</div></td>
                <td className="px-4 py-3 text-white">{fmt(r.amount, r.currency)}</td>
                <td className="px-4 py-3 text-slate-400">{r.demandeur}</td>
                <td className="px-4 py-3"><Status s={r.status} /></td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    {r.status === "soumise" && canValidate(user) && <>
                      <button onClick={() => decide(r.id, "approuvee")} className="p-2 rounded-lg text-emerald-400 hover:bg-[#1E293B]"><CheckCircle2 className="w-4 h-4" /></button>
                      <button onClick={() => decide(r.id, "refusee")} className="p-2 rounded-lg text-rose-400 hover:bg-[#1E293B]"><XCircle className="w-4 h-4" /></button>
                    </>}
                    {r.status === "approuvee" && <button onClick={() => convert(r.id)} data-testid={`request-convert-${r.id}`} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#3B82F6]/15 text-[#3B82F6] text-xs"><ArrowRightCircle className="w-3.5 h-3.5" /> Convertir</button>}
                  </div>
                </td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </Card>
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="dark bg-[#111827] border-[#1E293B]">
          <DialogHeader><DialogTitle className="text-white">Nouvelle demande de dépense</DialogTitle></DialogHeader>
          {form && <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Montant estimé *"><input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={inputCls} /></Field>
            <Field label="Catégorie"><select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls}><option value="">—</option>{cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}</select></Field>
            <Field label="Motif *" full><input value={form.motif} onChange={(e) => setForm({ ...form, motif: e.target.value })} className={inputCls} /></Field>
            <Field label="Bénéficiaire"><input value={form.beneficiaire} onChange={(e) => setForm({ ...form, beneficiaire: e.target.value })} className={inputCls} /></Field>
            <Field label="Date souhaitée"><input type="date" value={form.desired_date} onChange={(e) => setForm({ ...form, desired_date: e.target.value })} className={inputCls} /></Field>
            <Field label="Description" full><textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} /></Field>
          </div>}
          <DialogFooter><button onClick={() => setForm(null)} className="px-4 py-2.5 rounded-xl border border-[#1E293B] text-slate-300 text-sm">Annuler</button><button onClick={save} data-testid="request-save-button" className="px-5 py-2.5 rounded-xl bg-[#3B82F6] text-white text-sm font-semibold">Soumettre</button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
